import { useState, FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { forgotPassword, confirmForgotPassword } from '../auth/cognito';

/**
 * Two-step reset flow:
 *   1. "request" — collect email, ask Cognito to mail a 6-digit code
 *   2. "confirm" — user enters code + new password to finalize
 * Step is tracked locally; on success we route to /login with a
 * one-shot success banner.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledEmail = (location.state as { email?: string } | null)?.email ?? '';

  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState(prefilledEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setStep('confirm');
      setInfo(`We sent a 6-digit code to ${email}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start password reset');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await confirmForgotPassword(email, code.trim(), newPassword);
      navigate('/login', {
        replace: true,
        state: { email, info: 'Password updated. Sign in with your new password.' },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setError(null);
    setInfo(null);
    try {
      await forgotPassword(email);
      setInfo('A new code is on its way.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend code');
    }
  }

  return (
    <div className="auth-wrap">
      {step === 'request' ? (
        <form className="auth-card" onSubmit={handleRequest} noValidate>
          <h1>Reset your password</h1>
          <p className="lede">
            Enter the email on your account and we'll send you a code to set a new password.
          </p>

          {error && <div className="error-banner">{error}</div>}

          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send reset code'}
          </button>

          <div className="switch">
            Remembered it? <Link to="/login">Back to sign in</Link>
          </div>
        </form>
      ) : (
        <form className="auth-card" onSubmit={handleConfirm} noValidate>
          <h1>Set a new password</h1>
          <p className="lede">
            Enter the 6-digit code we sent to <strong>{email}</strong> and choose a new password.
          </p>

          {error && <div className="error-banner">{error}</div>}
          {info && <div className="info-banner">{info}</div>}

          <div className="field">
            <label htmlFor="code">Verification code</label>
            <input
              id="code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
              placeholder="123456"
              value={code} onChange={(e) => setCode(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password" type="password" required autoComplete="new-password"
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="confirm-password">Confirm new password</label>
            <input
              id="confirm-password" type="password" required autoComplete="new-password"
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Updating…' : 'Update password'}
          </button>

          <div className="switch">
            Didn't get it? Check your spam / junk folder for an email from{' '}
            <strong>no-reply@maximized.kids</strong>, or{' '}
            <button
              type="button"
              onClick={handleResend}
              style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', font: 'inherit' }}
            >
              resend the code
            </button>
            .
          </div>
        </form>
      )}
    </div>
  );
}
